import React from 'react';
import cn from 'classnames';
import IconFont from '$/components/IconFont';
import type { OfficialAssetServerGenre } from '$/api/server.types';
import { useLanguageStore } from '$/store/languageStore';

import styles from './index.module.scss';

export interface CategoryTabsProps {
  categories: OfficialAssetServerGenre[];
  selectedCategoryId: number | null;
  searchValue: string;
  onCategoryChange: (category: OfficialAssetServerGenre) => void;
  onSearchChange: (value: string) => void;
  onSearch: (value: string) => void;
}

/**
 * 官方资产素材分类栏，分类和搜索状态由父组件托管。
 */
function CategoryTabs({
  categories,
  selectedCategoryId,
  searchValue,
  onCategoryChange,
  onSearchChange,
  onSearch,
}: CategoryTabsProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);

  /** 搜索输入变化时仅同步输入值，不立即发起请求。 */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onSearchChange(event.target.value);
  };

  /** 搜索框失焦时提交当前关键词。 */
  const handleSearchBlur = (): void => {
    onSearch(searchValue);
  };

  /** 在搜索框中按回车时提交当前关键词。 */
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      onSearch(searchValue);
    }
  };

  /** 切换素材分类，父组件负责清空标签值和重置分页。 */
  const handleCategoryClick = (category: OfficialAssetServerGenre): void => {
    onCategoryChange(category);
  };

  return (
    <div className={styles.categoryTabs}>
      <div className={styles.categoryTabList}>
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            className={cn(styles.categoryTabItem, {
              [styles.categoryTabItemSelected]: category.id === selectedCategoryId,
            })}
            onClick={() => handleCategoryClick(category)}
          >
            {category.name}
          </button>
        ))}
      </div>
      <div className={styles.categorySearchBox}>
        <IconFont
          name="magnifyingGlassBold"
          fontSize={14}
          color="var(--content-tertiary, rgb(247 248 250 / 42%))"
          className={styles.categorySearchIcon}
        />
        <input
          type="text"
          className={styles.categorySearchInput}
          placeholder={languageStore.smartcatTranslationText((v) => v.search)}
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={handleSearchBlur}
          onKeyDown={handleSearchKeyDown}
        />
      </div>
    </div>
  );
}

export default CategoryTabs;
